'use client'

import { Card } from '@acko/card'
import { Accordion } from '@acko/accordion'
import { Typography } from '@acko/typography'
import { trackComprehensiveEngagement } from './ComprehensiveAnalyticsClient'

/* FAQ answers must match the FAQPage schema in schemas.ts exactly. */
const faqs = [
  {
    id: 'cfaq-1',
    q: 'What is comprehensive bike insurance and what does it cover?',
    a: 'Comprehensive bike insurance consists of own damage and compulsory third party. This includes coverage against accidental damage, theft, fire, flood, earthquake, cyclone, manmade damage, rat-bite damage, and transport damage.',
  },
  {
    id: 'cfaq-2',
    q: 'What is the difference between comprehensive and third-party bike insurance?',
    a: 'The third-party insurance will only cover injuries and damages you cause to other individuals. Comprehensive insurance, on the other hand, comes with own-damage coverage as well as other options, such as zero depreciation.',
  },
  {
    id: 'cfaq-3',
    q: 'Is comprehensive bike insurance mandatory in India?',
    a: 'No, comprehensive bike insurance is not mandatory in India. Under the Motor Vehicles Act, 1988, only third-party liability insurance is legally required for all two-wheelers.',
  },
  {
    id: 'cfaq-4',
    q: 'Which add-ons should I choose with my comprehensive bike insurance plan?',
    a: 'It is recommended that zero depreciation coverage, engine cover, and roadside assistance be included in your bike insurance plan because they provide protection against costly repairs.',
  },
  {
    id: 'cfaq-5',
    q: 'Does comprehensive bike insurance cover theft?',
    a: 'Yes, comprehensive bike insurance covers theft and burglary. If your bike is stolen, the insurer pays the Insured Declared Value (IDV), which is the current market value of your bike after depreciation.',
  },
  {
    id: 'cfaq-6',
    q: 'How is IDV calculated for comprehensive bike insurance?',
    a: "IDV in comprehensive bike insurance is calculated as the manufacturer's listed selling price of your bike minus depreciation based on its age. For a bike under 6 months old, depreciation is 5%; for 1–2 years, it is 20%; for 3–4 years, it is 40%; and for bikes older than 5 years, it is 50%.",
  },
  {
    id: 'cfaq-7',
    q: 'What is zero depreciation in comprehensive bike insurance?',
    a: 'Zero depreciation is an add-on for comprehensive bike insurance that pays the full replacement cost of damaged parts without any deduction for wear and tear. Without this add-on, standard policies deduct up to 50% from plastic and rubber parts and 10–40% from metal parts, leaving you to pay out of pocket.',
  },
  {
    id: 'cfaq-8',
    q: 'What is the validity of comprehensive bike insurance?',
    a: 'The validity of comprehensive bike insurance for a new bike is 1 year for own damage and 5 years for third-party components. The validity period of comprehensive bike insurance for old motorcycles is one year.',
  },
  {
    id: 'cfaq-9',
    q: 'Is comprehensive bike insurance better than own damage?',
    a: 'Yes, comprehensive bike insurance is usually a better choice compared to an own damage (OD) policy since a comprehensive bike insurance protects not only your vehicle but also includes the third-party legal risk, which is mandatory, whereas an own damage (OD) policy alone would require you to purchase and carry a separate third-party insurance to be legal on the roads.',
  },
  {
    id: 'cfaq-10',
    q: 'What happens to my NCB while renewing an expired policy?',
    a: 'Your NCB will be secured if you renew your expired insurance policy within 90 days; otherwise, it will be entirely lost and reset to 0% if it has expired for more than 90 days.',
  },
]

const trackFaqExpansion = (openValues: string[]) => {
  const openedFaq = faqs.find(faq => faq.id === openValues[0])
  if (openedFaq) void trackComprehensiveEngagement('FAQ', openedFaq.q)
}

export const ComprehensiveFAQSectionClient = () => (
  <div id="faq" className="flex flex-col gap-32 lg:gap-40 w-full scroll-mt-[104px]">
    <Typography scale="4xl" emphasis="bold" as="h2" color="primary">
      Frequently asked questions about comprehensive bike insurance
    </Typography>
    <Card variant="secondary" className="w-full px-24 py-16">
      {/* size="lg" is valid in @acko/css (larger trigger); the package type is stale, hence the cast */}
      <Accordion
        type="single"
        variant="lined"
        size={'lg' as unknown as 'default'}
        onChange={trackFaqExpansion}
        items={faqs.map(faq => ({ value: faq.id, trigger: faq.q, content: faq.a }))}
      />
    </Card>
  </div>
)
